/**
 * The line fabricator.
 *
 * Melts carried linelits back into standard line — which is the same trade in
 * reverse, since a linelit *is* a unit of line that got cut too short to be one.
 *
 * ── Why it lives with the mechanic ──────────────────────────────────────────
 *
 * It used to be a third tab in the station shop, beside the vault and the
 * trader, which put it with the people who sell you things. It is not a shop:
 * nothing here has a shelf, nothing is in stock, and the machine does not care
 * what the market is doing. It is a fitting on the station, run by the same
 * person who repairs your hull — so it sits under the mechanic, and appears only
 * where there is a fabricator to run.
 */

import { useState } from 'react';
import {
  FABRICATOR_MAX_LENGTH,
  canAfford,
  fabricate,
  fabricationCost,
  formatLinelits,
  linePrice,
  type Wallet,
} from '@interspacial/shared';

export interface FabricatorPanelProps {
  wallet: Wallet;
  /**
   * Ask the server to lay the line.
   *
   * The price is re-derived server-side from the same functions used to draw it
   * here, so what is shown is a quote rather than an instruction.
   */
  act: (path: string, body?: Record<string, unknown>) => Promise<boolean>;
}

export function FabricatorPanel({ wallet, act }: FabricatorPanelProps) {
  const [length, setLength] = useState(10);
  const [note, setNote] = useState<string | null>(null);

  const cost = fabricationCost(length);
  const affordable = canAfford(wallet, cost);

  const lay = () => {
    /*
     * Quoted here, decided there.
     *
     * The local check exists to say *why* in a sentence a player can act on —
     * the server's refusal is correct but terse, and "you cannot afford that"
     * is more useful with the number in it.
     */
    const quote = fabricate(length, wallet.carried);
    if (!quote.ok) {
      setNote(
        quote.refused === 'cannotAfford'
          ? `A ${length}u run costs ${formatLinelits(quote.cost)}L.`
          : `The machine will not make that: ${quote.refused}.`,
      );
      return;
    }

    void act('/fabricate', { length }).then((ok) => {
      if (ok) setNote(`Laid a ${length}u line for ${formatLinelits(quote.cost)}L.`);
    });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={hint}>
        Recombine broken Linelits back into a line.
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <input
          type="range"
          min={1}
          max={FABRICATOR_MAX_LENGTH}
          step={1}
          value={length}
          onChange={(event) => setLength(Number(event.target.value))}
          style={{ flex: 1 }}
        />
        <span style={{ width: 44, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
          {length}u
        </span>
      </div>

      <button
        type="button"
        onClick={lay}
        disabled={!affordable}
        style={{ ...actionStyle, opacity: affordable ? 1 : 0.4 }}
      >
        lay down · {formatLinelits(cost)}L
      </button>

      {/* The two rates do not agree, and pretending otherwise in the UI would be
          worse than saying so. */}
      <div style={{ ...hint, opacity: 0.4 }}>
        A trader sells the same length for {formatLinelits(linePrice(length))}L.
      </div>

      {note && <div style={{ ...hint, color: '#ffb347' }}>{note}</div>}
    </div>
  );
}

const actionStyle: React.CSSProperties = {
  flex: 1,
  padding: '6px 12px',
  background: 'rgba(8,14,24,0.82)',
  border: '1px solid rgba(127,212,255,0.22)',
  color: '#dbeaff',
  font: 'inherit',
  fontSize: 12,
  cursor: 'pointer',
};

const hint: React.CSSProperties = {
  fontSize: 11,
  opacity: 0.55,
  lineHeight: 1.45,
};
